import React from 'react';
import { translate } from 'react-i18next';

import SelfScheduleActions from 'actions/SelfScheduleActions.js';
import EmployeeActions from 'actions/EmployeeActions.js';
import ConfirmationActions from 'actions/ConfirmationActions.js';
import SelfScheduleCalendar, { emptyDay } from 'components/SelfScheduleCalendar.js';
import SelfScheduleSidebar from 'components/SelfScheduleSidebar.js';
import SelfScheduleToolbar from 'components/SelfScheduleToolbar.js';
import StatusDialogs from 'components/StatusDialogs.js';

// One pass over each source, keyed by the calendar date the row belongs to. Pool rows use the
// API's own `date` rather than the local date of starts_at, which for an overnight shift falls on
// the following day once converted. The roster and time off are there because eligible_shifts
// silently drops anything clashing with a shift the employee already holds, so without them a day
// they already work would render as simply empty.
function bucketByDay(blockData, rosterShifts, timeOff) {
  const days = {};
  const day = (date) => {
    if (!days[date]) days[date] = emptyDay();
    return days[date];
  };

  // An N/A day inside the block is an unavailable under the hood, so the month's time off carries
  // it as well; only the block's own row is rendered.
  const naIds = blockData.naEntries.map(entry => String(entry.id));

  blockData.shifts.forEach(shift => day(shift.date).shifts.push(shift));
  rosterShifts.forEach(shift => day(shift.date).roster.push(shift));
  timeOff
    .filter(entry => !_.contains(naIds, String(entry.id)))
    .forEach(entry => day(moment(entry.date).format('YYYY-MM-DD')).timeOff.push(entry));
  blockData.naEntries.forEach((entry) => {
    day(entry.date).naEntry = entry;
  });

  return days;
}

// What one block's page holds, reset whole when the route names another block. The toast stack
// is deliberately not part of it.
const blockState = () => ({
  // The block page as SelfScheduleStore resolved it, or null until the route's block has loaded.
  blockData: null,
  month: null,
  selectedDate: null,
  rosterShifts: [],
  timeOff: [],
  // The write in flight, {slot, toast}: `slot` is where a refusal renders ('day' beside the
  // selected day, 'submission' under the submission panel), and `toast` the success message key,
  // if the write gets one. One at a time; the sidebar disables its buttons meanwhile.
  writing: null,
  // The last refusal, {slot, messages, check}: a submit the check blocked carries its check, any
  // other refusal its sentences.
  writeError: null,
  error: null,
});

const monthRange = month => ({
  start: moment(month).startOf('month').format(),
  end: moment(month).endOf('month').format(),
});

class SelfScheduleView extends React.Component {
  constructor(props) {
    super(props);

    this._onBlockLoaded = this._onBlockLoaded.bind(this);
    this._onBlockError = this._onBlockError.bind(this);
    this._onOverlayLoaded = this._onOverlayLoaded.bind(this);
    this._onWriteSuccess = this._onWriteSuccess.bind(this);
    this._onWriteError = this._onWriteError.bind(this);
    this._onMonthChange = this._onMonthChange.bind(this);
    this._onSelectDate = this._onSelectDate.bind(this);
    this._onRequestShift = this._onRequestShift.bind(this);
    this._onCancelRequest = this._onCancelRequest.bind(this);
    this._onSubmit = this._onSubmit.bind(this);
    this._onReopen = this._onReopen.bind(this);
    this._closeStatusDialog = this._closeStatusDialog.bind(this);

    this.state = Object.assign({ statuses: [] }, blockState());
  }

  componentDidMount() {
    SelfScheduleStore.on('self_schedule:block:load:success', this._onBlockLoaded, this);
    SelfScheduleStore.on('self_schedule:block:load:error', this._onBlockError, this);
    SelfScheduleStore.on('self_schedule:write:success', this._onWriteSuccess, this);
    SelfScheduleStore.on('self_schedule:write:error', this._onWriteError, this);
    ShiftStore.on('shifts:load:success', this._onOverlayLoaded, this);
    UnavailablesStore.on('unavailables:load:success', this._onOverlayLoaded, this);

    this._loadBlock(this.props.params.id);
  }

  // The route is self-schedule(/blocks/:id), so switching blocks from the header dropdown reuses
  // this component instance rather than remounting it.
  componentWillReceiveProps(nextProps) {
    if (nextProps.params.id !== this.props.params.id) {
      Utils.removeSpinner();
      this.setState(blockState());
      this._loadBlock(nextProps.params.id);
    }
  }

  componentWillUnmount() {
    SelfScheduleStore.off(null, null, this);
    ShiftStore.off(null, null, this);
    UnavailablesStore.off(null, null, this);
    Utils.removeSpinner();
  }

  _loadBlock(blockId) {
    if (blockId && Utils.isOnline()) {
      Utils.enableGlobalSpinner();
      SelfScheduleActions.getBlockData(blockId);
    }
  }

  _loadOverlay(month) {
    const { start, end } = monthRange(month);

    EmployeeActions.getShifts(start, end);
    EmployeeActions.getUnavailables(start, end);
  }

  _onBlockLoaded(blockData) {
    // A response for a block the employee has since navigated away from would otherwise land on
    // top of the one the route names.
    if (blockData.blockId !== this.props.params.id) {
      return;
    }

    Utils.removeSpinner();

    // Every write refetches the block, so a reload of the block already on screen keeps the month
    // and day the employee is looking at. A new block opens on its first month rather than
    // today's: a block usually starts weeks out, and navigation is clamped to it anyway.
    if (this.state.blockData) {
      this.setState({ blockData, writing: null });
      return;
    }

    const month = moment(blockData.block.block_start_date).startOf('month');
    this.setState({ blockData, month, writing: null });
    this._loadOverlay(month);
  }

  _onBlockError(blockId) {
    // A late failure for a block the employee has since left.
    if (blockId !== this.props.params.id) {
      return;
    }

    Utils.removeSpinner();
    this.setState({ error: this.props.t('SelfScheduleView.load_error'), writing: null });
  }

  _onOverlayLoaded() {
    const { month } = this.state;
    if (!month) {
      return;
    }

    const { start, end } = monthRange(month);

    this.setState({
      rosterShifts: ShiftStore.getAllShifts(start, end).map(SelfScheduleStore.describeRosterShift),
      timeOff: UnavailablesStore.getApprovedUnavailables(),
    });
  }

  // ponytail: every write refetches the whole block (seven reads) rather than patching state from
  // the response. Progress, capacity and the held-row flags are all server-computed, so this is
  // the only way to be sure the calendar agrees with the API; narrow it to eligible_shifts and
  // progress if the round trip ever feels slow.
  _onWriteSuccess(blockId) {
    const { writing } = this.state;
    // A write for a block the employee has since left.
    if (!writing || blockId !== this.props.params.id) {
      return;
    }

    if (writing.toast) {
      this._addStatus(this.props.t('SelfScheduleView.success'), this.props.t(writing.toast));
    }
    this._loadBlock(this.props.params.id);
  }

  _onWriteError(blockId, refusal) {
    const { writing } = this.state;
    if (!writing || blockId !== this.props.params.id) {
      return;
    }

    Utils.removeSpinner();
    this.setState({ writing: null, writeError: Object.assign({ slot: writing.slot }, refusal) });
  }

  _write(writing, action) {
    // A write confirmed while another is in flight would swallow the first's outcome. The
    // confirmation modal's backdrop makes that hard to do; this is the guard of last resort.
    if (this.state.writing) {
      return;
    }

    Utils.enableGlobalSpinner();
    this.setState({ writing, writeError: null });
    action();
  }

  _onMonthChange(month) {
    this.setState({ month });
    this._loadOverlay(month);
  }

  // Also from the picks list, which can name a day in a month other than the one on screen. A
  // refused submit stays put: its findings are what the employee is clicking around to fix.
  _onSelectDate(selectedDate) {
    const { blockData, month, writeError } = this.state;
    this.setState({
      selectedDate,
      writeError: writeError && writeError.slot === 'day' ? null : writeError,
    });

    const target = moment(selectedDate).startOf('month');
    const inBlock = target.isBetween(
      moment(blockData.block.block_start_date).startOf('month'),
      moment(blockData.block.block_end_date).startOf('month'),
      'month',
      '[]',
    );
    if (inBlock && !target.isSame(month, 'month')) {
      this._onMonthChange(target);
    }
  }

  _onRequestShift(shift) {
    this._write({ slot: 'day' }, () => (
      SelfScheduleActions.requestShift(this.props.params.id, shift.id)
    ));
  }

  _onCancelRequest(shift) {
    const { t } = this.props;

    ConfirmationActions.confirm({
      header: t('SelfScheduleView.cancel_request_title'),
      body: t('SelfScheduleView.cancel_request_body', {
        shift: shift.label,
        date: Utils.formatLocaleDate(moment(shift.date)),
      }),
      confirmation_cb: () => this._write({ slot: 'day' }, () => (
        SelfScheduleActions.cancelRequest(this.props.params.id, shift.my_request_id)
      )),
    });
  }

  _onSubmit() {
    const { t } = this.props;

    ConfirmationActions.confirm({
      header: t('SelfScheduleView.submit_title'),
      body: t('SelfScheduleView.submit_body'),
      confirmation_cb: () => this._write(
        { slot: 'submission', toast: 'SelfScheduleView.submit_success' },
        () => SelfScheduleActions.submit(this.props.params.id),
      ),
    });
  }

  _onReopen() {
    const { t } = this.props;

    ConfirmationActions.confirm({
      header: t('SelfScheduleView.reopen_title'),
      body: t('SelfScheduleView.reopen_body'),
      confirmation_cb: () => this._write(
        { slot: 'submission', toast: 'SelfScheduleView.reopen_success' },
        () => SelfScheduleActions.reopen(this.props.params.id),
      ),
    });
  }

  _addStatus(title, message) {
    const status = {
      title, message, status: 'success', key: _.uniqueId('self-schedule-status-'),
    };
    this.setState(state => ({ statuses: state.statuses.concat(status) }));
  }

  _closeStatusDialog(props) {
    const { key } = props.status;
    this.setState(state => ({ statuses: state.statuses.filter(status => status.key !== key) }));
  }

  renderMessage(title, body) {
    return (
      <section id="schedule" className="self-schedule">
        <div className="self-schedule__empty">
          <h1>{title}</h1>
          <p>{body}</p>
        </div>
      </section>
    );
  }

  render() {
    const { t } = this.props;
    const {
      blockData, month, selectedDate, writeError,
    } = this.state;

    if (this.state.error) {
      return this.renderMessage(t('SelfScheduleView.title'), this.state.error);
    }

    if (!this.props.params.id) {
      return this.renderMessage(t('SelfScheduleView.title'), t('SelfScheduleView.select_a_block'));
    }

    if (!blockData) {
      return false;
    }

    const { block } = blockData;
    const days = bucketByDay(blockData, this.state.rosterShifts, this.state.timeOff);
    const firstMonth = moment(block.block_start_date).startOf('month');
    const lastMonth = moment(block.block_end_date).startOf('month');

    // id="schedule" is the calendar page's id, shared with the schedule view so the whole of
    // _calendar.scss and the brand overrides apply. The two never mount together.
    return (
      <section id="schedule" className="self-schedule">
        <SelfScheduleToolbar
          block={block}
          progress={blockData.progress}
          fteCounters={blockData.fteCounters}
          eligibleCount={blockData.eligibleCount}
          month={month}
          canGoBack={month.isAfter(firstMonth, 'month')}
          canGoForward={month.isBefore(lastMonth, 'month')}
          onMonthChange={this._onMonthChange}
        />
        <div id="schedule-content-container">
          <div id="schedule-content-wrapper">
            <SelfScheduleCalendar
              month={month}
              blockStart={block.block_start_date}
              blockEnd={block.block_end_date}
              days={days}
              selectedDate={selectedDate}
              onSelectDate={this._onSelectDate}
            />
            <SelfScheduleSidebar
              blockData={blockData}
              date={selectedDate}
              day={days[selectedDate] || emptyDay()}
              dayError={writeError && writeError.slot === 'day' ? writeError : null}
              submissionError={writeError && writeError.slot === 'submission' ? writeError : null}
              busy={!!this.state.writing}
              onSelectDate={this._onSelectDate}
              onRequestShift={this._onRequestShift}
              onCancelRequest={this._onCancelRequest}
              onSubmit={this._onSubmit}
              onReopen={this._onReopen}
            />
            <StatusDialogs statuses={this.state.statuses} onClose={this._closeStatusDialog} />
          </div>
        </div>
      </section>
    );
  }
}

export default translate()(SelfScheduleView);
